import { ListChecks, GanttChart } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import GenericCrudModule from "@/components/GenericCrudModule";
import ContextButton from "@/components/ContextButton";
import ProactiveWhisper from "@/components/ProactiveWhisper";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import PlansActionGantt from "@/components/PlansActionGantt";

const PRIO_COLORS = { P0: "#E24B4A", P1: "#EF9F27", P2: "#A1A1AA" };
const STATUT_COLORS = { planifie: "#A1A1AA", en_cours: "#378ADD", retard: "#E24B4A", clos: "#1D9E75" };

export default function PlansAction() {
  const { t } = useTranslation();
  const [view, setView] = useState("list");
  const today = new Date();
  const inThreeMonths = new Date(today.getTime() + 90 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return (
    <>
      <ProactiveWhisper module="plans" />
      <ThreeQPageHeader endpoint="/workspace/plans-action/top-priority" className="mb-4" />
      {/* V23.15.44 — Vue liste / vue Gantt */}
      <div className="flex items-center gap-1 border-b border-white/5 mb-4" data-testid="plans-view-tabs">
        <button
          onClick={() => setView("list")}
          className={`relative px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${view === "list" ? "text-white" : "text-zinc-400 hover:text-zinc-200"}`}
          data-testid="plans-view-list"
        >
          <ListChecks className="w-3.5 h-3.5" /> Liste
          {view === "list" && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#1D9E75]" />}
        </button>
        <button
          onClick={() => setView("gantt")}
          className={`relative px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${view === "gantt" ? "text-white" : "text-[#EF9F27] hover:text-[#FFB627]"}`}
          data-testid="plans-view-gantt"
        >
          <GanttChart className="w-3.5 h-3.5" /> Gantt
          <span className="text-[8px] uppercase tracking-widest px-1 py-0.5 rounded bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30">
            Nouveau
          </span>
          {view === "gantt" && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#EF9F27]" />}
        </button>
      </div>

      {view === "gantt" ? (
        <PlansActionGantt />
      ) : (
      <GenericCrudModule
      module={{
        path: "plans",
        title: t("saas.plans_action.title"),
        overline: t("saas.plans_action.overline"),
        subtitle: t("saas.plans_action.subtitle"),
        accent: "#1D9E75", icon: ListChecks, dataTestId: "plans",
      }}
      fields={[
        { key: "titre", label: t("saas.plans_action.field_titre"), required: true, span: 2,
          placeholder: t("saas.plans_action.field_titre_ph") },
        { key: "owner", label: t("saas.plans_action.field_owner") },
        { key: "echeance", label: t("saas.plans_action.field_echeance"), type: "date", required: true, default: inThreeMonths },
        { key: "priorite", label: t("saas.plans_action.field_priorite"), type: "select", default: "P1",
          options: ["P0", "P1", "P2"].map((v) => ({ value: v, label: t(`saas.plans_action.priorites.${v}`, { defaultValue: v }) })) },
        { key: "statut", label: t("saas.plans_action.field_statut"), type: "select", default: "planifie",
          options: ["planifie", "en_cours", "retard", "clos"].map((v) => ({ value: v, label: t(`saas.plans_action.statuts.${v}`) })) },
        { key: "progression", label: t("saas.plans_action.field_progression"), type: "number", default: 0, max: 100 },
        { key: "budget_keur", label: t("saas.plans_action.field_budget"), type: "number", default: 0 },
        { key: "description", label: t("saas.plans_action.field_description"), type: "textarea", span: 2 },
      ]}
      columns={[
        { key: "titre", label: t("saas.plans_action.col_plan") },
        { key: "owner", label: t("saas.plans_action.col_owner") },
        { key: "echeance", label: t("saas.plans_action.col_echeance") },
        { key: "priorite", label: t("saas.plans_action.col_priorite"), render: (r) => (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
            style={{ background: `${PRIO_COLORS[r.priorite]}1A`, color: PRIO_COLORS[r.priorite] }}>
            {t(`saas.plans_action.priorites.${r.priorite}`, { defaultValue: r.priorite })}
          </span>
        )},
        { key: "progression", label: t("saas.plans_action.col_progress"), render: (r) => (
          <div className="flex items-center gap-2">
            <div className="w-16 h-1 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full" style={{ width: `${r.progression}%`, background: STATUT_COLORS[r.statut] }} />
            </div>
            <span className="text-xs text-zinc-300">{r.progression}%</span>
          </div>
        )},
        { key: "statut", label: t("saas.plans_action.col_statut"), render: (r) => (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider"
            style={{ background: `${STATUT_COLORS[r.statut]}1A`, color: STATUT_COLORS[r.statut] }}>
            {t(`saas.plans_action.statuts.${r.statut}`, { defaultValue: r.statut })}
          </span>
        )},
      ]}
      emptyMsg={t("saas.plans_action.empty_msg")}
      primaryCta={t("saas.plans_action.primary_cta")}
      headerActions={<ContextButton moduleKey="plans" />}
      kpis={(data) => {
        const items = data.items || [];
        const total = items.length;
        const en_cours = items.filter((i) => i.statut === "en_cours").length;
        const retard = items.filter((i) => i.statut === "retard").length;
        const prog = items.length ? Math.round(items.reduce((s, i) => s + (i.progression || 0), 0) / items.length) : 0;
        return [
          { label: t("saas.plans_action.kpi_total"), value: total },
          { label: t("saas.plans_action.kpi_open"), value: en_cours, color: "#378ADD" },
          { label: t("saas.plans_action.kpi_late"), value: retard, color: "#E24B4A" },
          { label: t("saas.plans_action.kpi_progress"), value: `${prog}%`, color: prog >= 70 ? "#1D9E75" : "#EF9F27" },
        ];
      }}
    />
    )}
    </>
  );
}
